import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import MediaElement, { isVideoSrc } from './MediaElement';

const DEFAULT_ASPECT = '16 / 9';

const MediaFrame = forwardRef(function MediaFrame(
  { src, alt = '', className = '', mediaClassName = '', objectPosition, fit = 'natural', wrapperStyle, ...rest },
  ref
) {
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current, []);

  const [aspect, setAspect] = useState(null);

  useEffect(() => {
    setAspect(null);
  }, [src]);

  const captureNaturalAspect = () => {
    const el = innerRef.current;
    if (!el) return;
    let w = 0;
    let h = 0;
    if (typeof HTMLVideoElement !== 'undefined' && el instanceof HTMLVideoElement) {
      w = el.videoWidth;
      h = el.videoHeight;
    } else {
      w = el.naturalWidth;
      h = el.naturalHeight;
    }
    if (w > 0 && h > 0) {
      setAspect(`${w} / ${h}`);
    }
  };

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return undefined;
    if (typeof HTMLImageElement !== 'undefined' && el instanceof HTMLImageElement && el.complete) {
      captureNaturalAspect();
    }
    return undefined;
  }, [src]);

  const handleImageLoad = (e) => {
    captureNaturalAspect();
    if (rest.onLoad) rest.onLoad(e);
  };

  const handleVideoMetadata = (e) => {
    captureNaturalAspect();
    if (rest.onLoadedMetadata) rest.onLoadedMetadata(e);
  };

  const fitClass = fit === 'cover' ? 'object-cover' : 'object-contain';
  const style = {
    aspectRatio: aspect || DEFAULT_ASPECT,
    ...(wrapperStyle || {})
  };

  const mediaProps = {
    src,
    alt,
    className: `absolute inset-0 w-full h-full ${fitClass} ${mediaClassName}`.trim(),
    style: objectPosition ? { objectPosition } : undefined,
    ...rest
  };

  if (isVideoSrc(src)) {
    mediaProps.onLoadedMetadata = handleVideoMetadata;
  } else {
    mediaProps.onLoad = handleImageLoad;
  }

  return (
    <div className={`relative w-full overflow-hidden bg-slate-950 ${className}`} style={style}>
      <MediaElement ref={innerRef} {...mediaProps} />
    </div>
  );
});

export default MediaFrame;
